import React from 'react';

import classNames from 'classnames';

import { serializerFlexAlignment } from '../../utils/serializerFlexAlignment';

import type { CardSchema } from '../../authorsElement';
import type { PostCardSchema, PostData } from '../../types';
import type { TPostAttributes } from '../Post.types';
import Authors from './Authors';
import Image from './Image';
import Premium from './Premium';
import ReadTime from './ReadTime';
import Seperator from './Seperator';
import Subtitle from './Subtitle';
import Tags from './Tags';
import Timestamp from './Timestamp';
import Title from './Title';

const FLEX_OPTIONS: Record<string, string> = {
  left: 'flex-start',
  center: 'center',
  right: 'flex-end',
};

const premiumCheck = (post: PostData) => {
  const isPremiumCheckV1 = post.audience === 'premium'; // Deprecated check, but some tiptap states might still have this value and not .is_premium
  const isPremiumCheckV2 = post?.is_premium;

  return isPremiumCheckV1 || isPremiumCheckV2;
};

const cardSchema: PostCardSchema = {
  node: 'div',
  className: 'flex flex-col gap-3 w-full w-full',
  children: [
    {
      node: 'image',
      className: '',
      children: [],
    },
    {
      node: 'div',
      className: 'flex flex-col gap-2',
      children: [
        {
          node: 'tags',
          className: '',
          children: [],
        },
        {
          node: 'div',
          className: 'flex flex-col gap-1',
          children: [
            {
              node: 'title',
              className: '',
              children: [],
            },
            {
              node: 'subtitle',
              className: '',
              children: [],
            },
            {
              node: 'authors',
              className: 'text-xs',
              children: [],
            },
            {
              node: 'div',
              className: 'flex flex-row gap-1 items-center flex-wrap',
              children: [
                {
                  node: 'timestamp',
                  className: '',
                  children: [],
                },
                {
                  node: 'premium',
                  className: '',
                  children: [],
                },
              ],
            },
          ],
        },
      ],
    },
  ],
};

interface SerializerProps {
  post: PostData;
  attributes: TPostAttributes;
}

const Serializer: React.FC<SerializerProps> = ({ post, attributes }) => {
  const renderNode = (node: CardSchema): React.ReactNode => {
    switch (node.node) {
      case 'div': {
        const flexAlignmentClass = serializerFlexAlignment({
          alignment: attributes?.textAlign || ('left' as any),
          includesFlexRow: node.className.includes('flex-row'),
          includesFlexColumn: node.className.includes('flex-col'),
        });

        return (
          <div className={classNames(node.className, flexAlignmentClass)}>
            {node.children.map((child, index) => (
              <React.Fragment key={index}>{renderNode(child)}</React.Fragment>
            ))}
          </div>
        );
      }
      case 'figure':
        return (
          <figure className={classNames(node.className)}>
            {node.children.map((child, index) => (
              <React.Fragment key={index}>{renderNode(child)}</React.Fragment>
            ))}
          </figure>
        );
      case 'image':
        return attributes?.imageEnabled && !attributes?.hasBackgroundImage ? (
          <Image
            alt={post.web_title}
            src={post.image_url}
            style={{
              ...(attributes?.imageSizeFixed
                ? {
                    width: attributes?.imageWidth,
                    height: attributes?.imageHeight,
                    minHeight: attributes?.imageHeight,
                    minWidth: attributes?.imageWidth,
                  }
                : {}),
              borderRadius: attributes?.imageBorderRadius,
            }}
            className={classNames(
              'w-full object-cover h-auto',
              node.className,
              attributes?.imageSizeFixed !== true && 'aspect-[16/9]',
            )}
          />
        ) : null;
      case 'tags':
        if (!attributes?.tagsEnabled) return null;

        return (
          <Tags
            className={node.className}
            tags={post?.content_tags}
            postCategory={attributes?.postsCategory}
            tagStyle={{
              textAlign: attributes?.textAlign as 'left' | 'center' | 'right' | undefined,
              textTransform: attributes?.tagTransform,
              badgeEnabled: attributes?.tagBadgeEnabled,
            }}
          />
        );
      case 'title':
        return (
          <Title
            title={post?.web_title}
            className={classNames(
              attributes?.titleTransform || 'normal-case',
              attributes?.titleFontSize || 'text-lg',
              attributes?.titleLineClamp || 'line-clamp-none',
              attributes?.hoverTitleUnderline &&
                'group-hover:underline group-hover:decoration-[var(--hover-title-underline-color)]',
              'group-hover:text-[var(--hover-title-color)]',
              node.className,
            )}
          />
        );
      case 'subtitle':
        return attributes?.subtitleShowing && post.web_subtitle ? (
          <Subtitle
            subtitle={post.web_subtitle}
            className={classNames(
              attributes?.subtitleTransform || 'normal-case',
              attributes?.subtitleFontSize || 'text-lg',
              attributes?.subtitleLineClamp || 'line-clamp-none',
              attributes?.hoverSubtitleUnderline &&
                'group-hover:underline group-hover:decoration-[var(--hover-subtitle-underline-color)]',
              'group-hover:text-[var(--hover-subtitle-color)]',
              node.className,
            )}
          />
        ) : null;
      case 'authors':
        return attributes?.authorsEnabled ? (
          <Authors
            className={node.className}
            authors={post?.authors}
            alignment={attributes?.textAlign as 'center' | 'left' | 'right' | undefined}
            byLineText={attributes?.byLineText}
            displayImages={attributes?.authorsImagesEnabled}
            displayText={attributes?.authorsTextEnabled}
            truncateList={attributes?.authorsTruncateListEnabled}
            authorsTextTransform={attributes?.authorsTextTransform}
            authorsFontSize={attributes?.authorsFontSize}
            authorContainerStyle={{
              justifyContent: FLEX_OPTIONS[attributes?.textAlign || 'left'],
            }}
          />
        ) : null;
      case 'timestamp':
        return attributes?.timestampsEnabled && post?.override_scheduled_at ? (
          <Timestamp
            className={node.className}
            timestamp={post?.override_scheduled_at}
            timestampDisplay={attributes?.timestampsDisplay}
            alignment={attributes?.textAlign as 'left' | 'center' | 'right' | undefined}
          />
        ) : null;
      case 'read_time':
        return attributes?.readTimeEnabled && post?.estimated_reading_time_display ? (
          <ReadTime className={node.className}>{post?.estimated_reading_time_display}</ReadTime>
        ) : null;
      case 'premium': {
        const showPremiumBadge =
          premiumCheck(post) &&
          (Boolean(attributes?.premiumBadgeEnabled) ||
            Boolean(attributes?.premiumIconEnabled) ||
            Boolean(attributes?.premiumTextEnabled));

        return showPremiumBadge ? (
          <Premium
            text="Paid"
            style={{
              textTransform: attributes?.premiumTextTransform,
            }}
            className={node.className}
            isBadge={Boolean(attributes?.premiumBadgeEnabled)}
            hasIcon={Boolean(attributes?.premiumIconEnabled)}
            hasText={Boolean(attributes?.premiumTextEnabled)}
          />
        ) : null;
      }
      case 'line-separator':
      case 'dot-separator':
      case 'dash-separator':
      case 'slash-separator':
        return <Seperator type={node.node} className={node.className} />;
      default:
        return null;
    }
  };

  return <>{renderNode(attributes?.cardStructure || cardSchema)}</>;
};

export default Serializer;
